import { z } from 'zod';
import {
  CALL_OUTCOMES, CONTACT_ROLES, DIRECTIONS, LEAD_SOURCES, LIFECYCLE_STAGES, LOGGABLE_ACTIVITY_TYPES, PRIORITIES,
  STAGE_KINDS, TASK_KINDS, USER_ROLES,
} from './enums';
import { normalizePhone } from './phone';

// ─── Reusable field schemas (Hebrew messages) ──────────────────────────
const req = (label: string, max = 200) =>
  z.string({ error: `${label} הוא שדה חובה` }).trim().min(1, `${label} הוא שדה חובה`).max(max, `${label} ארוך מדי`);
const opt = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, 'הערך ארוך מדי')
    .optional()
    .nullable()
    .transform((v) => (v === '' ? null : v));

export const uuid = z.uuid({ error: 'מזהה לא תקין' });
const optUuid = uuid.optional().nullable().or(z.literal('').transform(() => null));

export const phoneField = z
  .string()
  .trim()
  .max(40)
  .optional()
  .nullable()
  .transform((v, ctx) => {
    if (v === undefined) return undefined;
    if (!v) return null;
    const n = normalizePhone(v);
    if (!n) {
      ctx.addIssue({ code: 'custom', message: 'מספר טלפון לא תקין' });
      return z.NEVER;
    }
    return v;
  });

export const emailField = z
  .string()
  .trim()
  .max(254)
  .optional()
  .nullable()
  .transform((v) => (v === undefined ? undefined : v ? v.toLowerCase() : null))
  .refine((v) => v == null || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), 'כתובת אימייל לא תקינה');

const money = z.coerce.number({ error: 'יש להזין מספר' }).min(0, 'הסכום חייב להיות חיובי').max(100_000_000);
const optMoney = money.optional().nullable();
const pct = z.coerce.number().min(0, 'אחוז בין 0 ל-100').max(100, 'אחוז בין 0 ל-100');
const isoDateTime = z.coerce.date({ error: 'תאריך לא תקין' });
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'תאריך לא תקין');

// ─── Auth & users ──────────────────────────────────────────────────────
export const loginSchema = z.strictObject({
  email: z.string().trim().toLowerCase().email('כתובת אימייל לא תקינה').max(254),
  password: z.string().min(1, 'יש להזין סיסמה').max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const passwordField = z
  .string()
  .min(10, 'הסיסמה חייבת להכיל לפחות 10 תווים')
  .max(200, 'הסיסמה ארוכה מדי');

export const userCreateSchema = z.strictObject({
  email: z.string().trim().toLowerCase().email('כתובת אימייל לא תקינה').max(254),
  fullName: req('שם מלא', 120),
  phone: phoneField,
  role: z.enum(USER_ROLES),
  password: passwordField,
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = z.strictObject({
  fullName: req('שם מלא', 120).optional(),
  phone: phoneField,
  role: z.enum(USER_ROLES).optional(),
  isActive: z.boolean().optional(),
  password: passwordField.optional(),
});
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

export const changePasswordSchema = z.strictObject({
  currentPassword: z.string().min(1, 'יש להזין את הסיסמה הנוכחית'),
  newPassword: passwordField,
});

// ─── Contacts ──────────────────────────────────────────────────────────
export const contactSchema = z.strictObject({
  fullName: req('שם איש הקשר', 120),
  role: z.enum(CONTACT_ROLES).default('other'),
  position: opt(120),
  phone: phoneField,
  whatsapp: phoneField,
  email: emailField,
  isPrimary: z.boolean().optional(),
  notes: opt(2000),
});
export type ContactInput = z.infer<typeof contactSchema>;
export const contactUpdateSchema = contactSchema.partial();

// ─── Organizations (leads & customers) ─────────────────────────────────
const orgFields = {
  name: req('שם העסק', 200),
  legalName: opt(200),
  vatId: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => (v === undefined ? undefined : v ? v.replace(/\D/g, '') : null))
    .refine((v) => v == null || /^\d{8,9}$/.test(v),'ח.פ. / ע.מ. חייב להכיל 8–9 ספרות'),
  businessType: opt(80),
  branchesCount: z.coerce.number().int().min(1, 'לפחות סניף אחד').max(10_000).optional().nullable(),
  address: opt(300),
  city: opt(80),
  website: opt(300),
  source: z.enum(LEAD_SOURCES),
  priority: z.enum(PRIORITIES),
  ownerId: optUuid,
  estimatedValue: optMoney,
  estimatedMonthlyKg: z.coerce.number().min(0).max(100_000).optional().nullable(),
  paymentTerms: opt(120),
  shippingTerms: opt(200),
  defaultDiscountPct: pct.optional().nullable(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  notes: opt(5000),
};

export const organizationCreateSchema = z.strictObject({
  ...orgFields,
  source: z.enum(LEAD_SOURCES).default('manual'),
  priority: z.enum(PRIORITIES).default('normal'),
  lifecycleStage: z.enum(LIFECYCLE_STAGES).default('lead'),
  pipelineStageId: optUuid,
  contact: contactSchema.optional().nullable(),
  /** Optional first note. */
  note: opt(5000),
  /** First follow-up due date (ISO). Leads without it get a default "tomorrow" follow-up. */
  firstFollowupAt: isoDateTime.optional().nullable(),
});
export type OrganizationCreateInput = z.infer<typeof organizationCreateSchema>;

/** PATCH semantics: every field optional, no defaults (an omitted field is left unchanged). */
export const organizationUpdateSchema = z
  .strictObject({ ...orgFields, lostReason: opt(500) })
  .partial();
export type OrganizationUpdateInput = z.infer<typeof organizationUpdateSchema>;

export const organizationListQuerySchema = z.object({
  lifecycle: z.enum(LIFECYCLE_STAGES).optional(),
  stageId: uuid.optional(),
  ownerId: z.union([uuid, z.literal('me'), z.literal('none')]).optional(),
  source: z.enum(LEAD_SOURCES).optional(),
  priority: z.enum(PRIORITIES).optional(),
  city: z.string().trim().max(80).optional(),
  q: z.string().trim().max(100).optional(),
  followup: z.enum(['overdue', 'today', 'none', 'upcoming']).optional(),
  /** open = active stages, closed = won/lost stages (e.g. "לא רלוונטי"), all = both. */
  status: z.enum(['open', 'closed', 'all']).optional(),
  /** Registration date range (Israel calendar days, inclusive). */
  registeredFrom: isoDay.optional(),
  registeredTo: isoDay.optional(),
  sort: z.enum(['next_followup', 'created', 'registered', 'last_order', 'last_contact', 'name', 'value']).default('next_followup'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type OrganizationListQuery = z.infer<typeof organizationListQuerySchema>;

export const stageMoveSchema = z.strictObject({
  stageId: uuid,
  lostReason: opt(500),
});

export const convertSchema = z.strictObject({
  customerSince: isoDay.optional(),
  note: opt(2000),
});

// ─── Activities ────────────────────────────────────────────────────────
export const activityCreateSchema = z.strictObject({
  type: z.enum(LOGGABLE_ACTIVITY_TYPES),
  contactId: optUuid,
  direction: z.enum(DIRECTIONS).optional().nullable(),
  subject: opt(200),
  body: opt(10_000),
  occurredAt: isoDateTime.optional(),
  callOutcome: z.enum(CALL_OUTCOMES).optional().nullable(),
  /** Schedule the next follow-up in the same request (the "log + reschedule" flow). */
  nextFollowupAt: isoDateTime.optional().nullable(),
  /** Completes this open follow-up task as part of logging the activity. */
  completeTaskId: optUuid,
});
export type ActivityCreateInput = z.infer<typeof activityCreateSchema>;

// ─── Tasks / follow-ups / meetings ─────────────────────────────────────
export const taskCreateSchema = z
  .strictObject({
    kind: z.enum(TASK_KINDS).default('followup'),
    organizationId: optUuid,
    contactId: optUuid,
    assigneeId: optUuid,
    title: req('כותרת', 200),
    description: opt(5000),
    dueAt: isoDateTime,
    endsAt: isoDateTime.optional().nullable(),
    allDay: z.boolean().default(false),
    location: opt(300),
  })
  .refine((t) => !t.endsAt || t.endsAt >= t.dueAt, { message: 'שעת הסיום לפני שעת ההתחלה', path: ['endsAt'] });
export type TaskCreateInput = z.infer<typeof taskCreateSchema>;

export const taskUpdateSchema = z.strictObject({
  title: req('כותרת', 200).optional(),
  description: opt(5000),
  dueAt: isoDateTime.optional(),
  endsAt: isoDateTime.optional().nullable(),
  allDay: z.boolean().optional(),
  location: opt(300),
  assigneeId: optUuid,
  contactId: optUuid,
});

export const taskCompleteSchema = z.strictObject({
  outcome: opt(2000),
  /** Schedule the next follow-up immediately. null = explicitly no next follow-up. */
  next: z
    .strictObject({ dueAt: isoDateTime, title: opt(200) })
    .optional()
    .nullable(),
});

export const taskListQuerySchema = z.object({
  assigneeId: z.union([uuid, z.literal('me'), z.literal('all')]).default('me'),
  status: z.enum(['open', 'done', 'cancelled', 'all']).default('open'),
  kind: z.enum(TASK_KINDS).optional(),
  organizationId: uuid.optional(),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});

// ─── Pipeline configuration ────────────────────────────────────────────
export const pipelineStageSchema = z.strictObject({
  name: req('שם השלב', 60),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'צבע לא תקין').default('#6b8f3a'),
  kind: z.enum(STAGE_KINDS).default('open'),
});
export const pipelineReorderSchema = z.strictObject({ stageIds: z.array(uuid).min(1).max(50) });

// ─── Inbound leads (website/Meta/API) ──────────────────────────────────
export const attributionSchema = z.object({
  utm_source: z.string().max(200).optional().nullable(),
  utm_medium: z.string().max(200).optional().nullable(),
  utm_campaign: z.string().max(300).optional().nullable(),
  utm_content: z.string().max(300).optional().nullable(),
  utm_term: z.string().max(300).optional().nullable(),
  gclid: z.string().max(300).optional().nullable(),
  fbclid: z.string().max(500).optional().nullable(),
  ttclid: z.string().max(300).optional().nullable(),
  landing_page: z.string().max(2000).optional().nullable(),
  referrer: z.string().max(2000).optional().nullable(),
});

/** Canonical inbound lead, produced by every source adapter before intake. */
export const inboundLeadSchema = z.object({
  externalId: z.string().min(1).max(200),
  submittedAt: isoDateTime.optional(),
  contactName: z.string().trim().max(200).optional().nullable(),
  businessName: z.string().trim().max(200).optional().nullable(),
  phone: z.string().trim().max(60).optional().nullable(),
  whatsapp: z.string().trim().max(60).optional().nullable(),
  email: z.string().trim().max(254).optional().nullable(),
  businessType: z.string().trim().max(120).optional().nullable(),
  city: z.string().trim().max(120).optional().nullable(),
  branches: z.string().trim().max(60).optional().nullable(),
  message: z.string().trim().max(10_000).optional().nullable(),
  productInterest: z.string().trim().max(300).optional().nullable(),
  expectedQuantity: z.string().trim().max(200).optional().nullable(),
  formName: z.string().trim().max(200).optional().nullable(),
  pageUrl: z.string().trim().max(2000).optional().nullable(),
  attribution: attributionSchema.optional().nullable(),
});
export type InboundLead = z.infer<typeof inboundLeadSchema>;

export const duplicateResolveSchema = z.strictObject({
  action: z.enum(['attach', 'create_new', 'reject']),
  organizationId: optUuid,
});

export const searchQuerySchema = z.object({ q: z.string().trim().min(1).max(100) });
